/**
 * The server refuses with one of two machine-readable prefixes when a
 * workspace requires two-step verification:
 *
 *   mfa_required:      the session has not passed a second step at all
 *   step_up_required:  it has, but more than 12 hours ago, and the action is
 *                      sensitive (export, removing members, security settings)
 *
 * Database errors carry the prefix in `message`; edge functions carry it in
 * the error text and, for mfa_required, in `code`. This turns either into one
 * answer the UI can act on.
 */

export type VerificationNeed = 'mfa_required' | 'step_up_required' | 'enrolment_required';

function textOf(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (error !== null && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const parts = [record.message, record.error, record.code, record.details]
      .filter((part): part is string => typeof part === 'string');
    return parts.join(' ');
  }
  return '';
}

export function verificationNeed(error: unknown): VerificationNeed | null {
  const text = textOf(error);
  if (/\bstep_up_required\b/.test(text)) return 'step_up_required';
  // An owner-only security action that needs the owner's OWN authenticator.
  // A company sign-in does not count here, so an owner who only uses SSO is
  // offered setup rather than "sign in again".
  if (/\bmfa_enrolment_required\b/.test(text)) return 'enrolment_required';
  if (/\bmfa_required\b/.test(text)) return 'mfa_required';
  return null;
}

export const STEP_UP_EXPLAINER =
  'For your security, this needs a code from your authenticator app entered in the last 12 hours.';

export const MFA_REQUIRED_EXPLAINER =
  'This workspace requires two-step verification. Enter a code from your authenticator app to continue.';

export const ENROLMENT_REQUIRED_EXPLAINER =
  'This needs your own authenticator app, even if you sign in through your company login. Set one up (it takes about a minute) or enter a code, then try again.';

export function verificationExplainer(need: VerificationNeed): string {
  if (need === 'step_up_required') return STEP_UP_EXPLAINER;
  if (need === 'enrolment_required') return ENROLMENT_REQUIRED_EXPLAINER;
  return MFA_REQUIRED_EXPLAINER;
}
